import type { BuiltReport, DiagnosticEnvelope } from './types';
import { getBreadcrumbs, getErrors } from './buffers';
import { collectAppContext, getRoute, getStaticInfo, getUser } from './context';

/**
 * Assemblage de l'enveloppe de diagnostic AU CLIC (jamais en passif).
 *
 * L'enveloppe = un préambule lisible (destiné à l'humain qui reçoit le rapport) +
 * le bloc JSON de l'enveloppe. Elle ne contient que des métadonnées : aucun mot de
 * passe, cookie, corps de requête ni texte utilisateur (hors la description que
 * l'utilisateur écrit lui-même).
 */

/** Réassurance affichée dans le préambule (pas de secret dans le rapport). */
export const NO_PASSWORDS_NOTE = 'Ce rapport ne contient pas vos mots de passe.';

/** Clé de persistance du thème (miroir de `theme/theme.ts`, sans dépendance React). */
const THEME_STORAGE_KEY = 'xfile.theme';

/** 6 hexa aléatoires (préfixés) : identifiant de rapport court et lisible. */
export function buildReportId(): string {
  const bytes = new Uint8Array(3);
  const cryptoObj = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `RPT-${hex}`;
}

/**
 * Scinde une version hybride `1.2.3+build456` en `{ version, buildId }`. Si la
 * version ne porte pas de `+build`, on garde le `buildId` fourni par ailleurs.
 */
export function splitVersion(
  version: string,
  fallbackBuildId: string | null,
): { version: string; buildId: string | null } {
  const plus = version.indexOf('+');
  if (plus >= 0) return { version: version.slice(0, plus), buildId: version.slice(plus + 1) };
  return { version, buildId: fallbackBuildId };
}

function readTheme(): string {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) ?? 'system';
  } catch {
    return 'system';
  }
}

function readTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function readLocale(): string {
  const docLang = typeof document !== 'undefined' ? document.documentElement.lang : '';
  return docLang || (typeof navigator !== 'undefined' ? navigator.language : '') || 'fr';
}

interface BuildReportOptions {
  /** Identifiant figé (stable pendant l'édition de la description). */
  reportId?: string;
  /** Instant de capture figé (stable pendant l'édition de la description). */
  capturedAt?: Date;
}

/** Construit le rapport complet à partir des anneaux + du contexte courant. */
export function buildReport(description = '', opts: BuildReportOptions = {}): BuiltReport {
  const reportId = opts.reportId ?? buildReportId();
  const capturedAt = opts.capturedAt ?? new Date();

  const info = getStaticInfo();
  const { version, buildId } = splitVersion(info.version, info.buildId);
  const breadcrumbs = getBreadcrumbs();

  const envelope: DiagnosticEnvelope = {
    app: info.app,
    version,
    buildId,
    hostname: info.hostname,
    reportId,
    time: capturedAt.toISOString(),
    tz: readTimeZone(),
    env: {
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      viewport:
        typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '0x0',
      locale: readLocale(),
      theme: readTheme(),
    },
    user: getUser(),
    appContext: collectAppContext(),
    route: getRoute(),
    description,
    breadcrumbs,
    api: breadcrumbs
      .filter((b) => b.type === 'api')
      .map((b) => {
        const entry: DiagnosticEnvelope['api'][number] = {};
        if (b.method !== undefined) entry.method = b.method;
        if (b.path !== undefined) entry.path = b.path;
        if (b.status !== undefined) entry.status = b.status;
        if (b.ms !== undefined) entry.ms = b.ms;
        if (b.requestId !== undefined) entry.requestId = b.requestId;
        if (b.count !== undefined) entry.count = b.count;
        return entry;
      }),
    errors: getErrors(),
  };

  const json = JSON.stringify(envelope, null, 2);
  const preamble = buildPreamble(envelope, capturedAt, description);
  return { reportId, preamble, envelope, json, text: `${preamble}\n${json}` };
}

/** Préambule humain : entête, date+fuseau, version/build/hôte, réf., note, description. */
function buildPreamble(
  envelope: DiagnosticEnvelope,
  capturedAt: Date,
  description: string,
): string {
  // Build (sha) annexé à la version SAUF en dev (`buildId` absent ou 'dev').
  const showBuild = envelope.buildId !== null && envelope.buildId !== 'dev';
  const versionPart = showBuild ? `${envelope.version}+${envelope.buildId}` : envelope.version;
  const hostPart = envelope.hostname ? ` · ${envelope.hostname}` : '';
  const lines = [
    `=== Rapport de bug — ${envelope.app} ===`,
    `Date : ${capturedAt.toISOString()} (${envelope.tz})`,
    `Version : ${envelope.app} ${versionPart}${hostPart}`,
    `Réf. : ${envelope.reportId}`,
    `Note : ${NO_PASSWORDS_NOTE}`,
    '',
    'Description (à remplir par vous) :',
    quoteDescription(description),
    '',
    '--- Contexte technique (ne pas modifier) ---',
  ];
  return lines.join('\n');
}

/** Préfixe chaque ligne de la description d'un `> ` (description vide → `>`). */
function quoteDescription(description: string): string {
  const trimmed = description.trim();
  if (!trimmed) return '>';
  return trimmed
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
}
